import { useEffect, useRef, useState } from 'react';
import { useStickyLayer } from '@/components/layout/StickyOffsetsProvider';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'next-themes';
import {
  Menu, LogOut, User as UserIcon, Sun, Moon, Keyboard, HelpCircle,
  BookOpen, Bot, Search, Plus, Check, Building2, Globe,
  FileText, Users, Receipt, Briefcase, Package,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, unwrapData } from '@/utils/api';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuRadioGroup, DropdownMenuRadioItem,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink,
  BreadcrumbPage, BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { NotificationCenter } from '@/components/notifications/NotificationCenter';
import { useAriaNudge } from '@/components/notifications/useAriaNudge';
import BugReportWidget from '@/components/feedback/BugReportWidget';
import AriaPulse from '@/components/ai/v2/AriaPulse';
import AiCreditsMeter from '@/components/plan/AiCreditsMeter';
import { createPageUrl } from '@/components/utils';
import { safeStorage } from '@/lib/safeStorage';
import { useDensity } from '@/lib/useDensity';
import { useSurfaceStyle } from '@/lib/useSurfaceStyle';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { cn, setDisplayLocale, getDisplayLocale, DISPLAY_LOCALES } from '@/lib/utils';
import AvisoPendientes, { avisoSePinta, contenidoDelBadge, textoAccesibleDelAviso } from './AvisoPendientes';

/**
 * Redesigned TopBar (PR4 — chrome-only).
 *
 * Visual changes:
 *   - Single-row layout, breadcrumb-led, subtle.
 *   - ⌘K trigger adapts to the available width.
 *   - IA toggle that opens the lateral AIPanel (replaces the AIAssistant
 *     embed — the old component stays on disk but is no longer mounted).
 *   - Density picker inside the user menu.
 *
 * Stable testids preserved: layout-header, command-menu-trigger.
 * The breadcrumb still shows the page title text so existing
 * `toContainText('Panel'|'Facturas'|...)` assertions keep passing.
 */
export default function TopBar({
  currentPageName,
  breadcrumbPath,
  pageTitleKey,
  user,
  /**
   * (b3) · El número del aviso de pendientes VERI*FACTU.
   *
   * Llega desde `Layout`, que es quien lo consulta, en vez de pedirlo aquí: dos
   * componentes consultando lo mismo producirían dos números que pueden diferir
   * durante un refresco — y dos cifras que se contradicen sobre lo mismo son peores
   * que una. Por defecto 0, para que un montaje sin la prop no pinte nada.
   */
  avisoStats = null,
  setMobileMenuOpen,
  setShowCommandMenu,
  setShowKeyboardShortcuts,
  onStartTour,
  onLogout,
  aiPanelOpen,
  onToggleAIPanel,
  onSwitchCompany,
  currentCompany,
  isAriaPage = false,
  alignedWorkspace = false,
}) {
  const headerRef = useRef(null);
  useStickyLayer('topbar', headerRef);
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  // Panel de notificaciones: el estado vive aquí (no dentro de la campana) para
  // que el aviso de bienvenida pueda abrirlo.
  const [bellOpen, setBellOpen] = useState(false);
  useAriaNudge(() => setBellOpen(true));
  const { density, setDensity, isOverridden, tenantDefault, clearOverride } = useDensity();
  const {
    surface,
    setSurface,
    isOverridden: surfaceOverridden,
    tenantDefault: surfaceTenantDefault,
    clearOverride: clearSurface,
  } = useSurfaceStyle();
  const { preferences, update: updatePrefs } = useUserPreferences();

  // Cross-device hydration del idioma: la preferencia por-usuario manda sobre el
  // caché local. Si difiere del idioma activo, aplicarla (una vez al cargar/cambiar).
  useEffect(() => {
    const pref = preferences?.locale_override;
    if ((pref === 'es' || pref === 'en') && pref !== i18n.language) {
      i18n.changeLanguage(pref);
      safeStorage.setItem('verix_lang', pref);
    }
  }, [preferences?.locale_override, i18n]);

  // Hidratación cross-device del formato de fecha/número (display_locale) — mismo
  // patrón que locale_override arriba, pero controla el FORMATO de presentación
  // (DD/MM vs MM/DD, separadores), no el idioma de los textos.
  useEffect(() => {
    const pref = preferences?.display_locale;
    if (pref && pref !== getDisplayLocale()) {
      setDisplayLocale(pref);
    }
  }, [preferences?.display_locale]);

  // #815 — empresas que el usuario puede CONMUTAR. Fuente: GET /api/auth/companies
  // (cross-tenant, el MISMO endpoint que el CompanySwitcher usa para pintar la
  // lista). NO usar /api/user-companies: es un CRUD tenant-scoped (filtra por la
  // company activa) que devuelve ≤1 fila SIEMPRE → el trigger no aparecía nunca.
  // staleTime 5 min porque el set de empresas raramente cambia durante la sesión.
  const { data: switchableCompanies = [], isLoading: switchableLoading } = useQuery({
    // eslint-disable-next-line local/no-literal-querykey -- key standalone del switcher-count, compartida con useUserCompaniesCount; la migración a qk.ts cubre solo Expense (Phase 1)
    queryKey: ['switchable-companies', user?.id],
    queryFn: async () => unwrapData(await apiFetch('/api/auth/companies')),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });
  const switchableCount = Array.isArray(switchableCompanies) ? switchableCompanies.length : 0;
  // #815 — gate por `!switchableLoading` para evitar el flash "trigger ausente →
  // aparece": durante la carga data es [] → no renderiza; aparece UNA vez con el
  // valor correcto tras resolver.
  const canSwitchCompany =
    !switchableLoading && switchableCount > 1 && typeof onSwitchCompany === 'function';

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');
  const toggleLanguage = () => {
    const next = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(next);
    safeStorage.setItem('verix_lang', next);
    updatePrefs({ locale_override: next }); // persiste cross-dispositivo
  };

  const pageTitle = pageTitleKey ? t(pageTitleKey) : currentPageName;

  return (
    <>
    {/* Scrim del hueco de scroll: el pill flotante (sticky top-3) deja 12px
        entre el viewport y su borde por donde el contenido pasa sin blur
        (franja blanca sobre el TopBar). Este strip pinta el mismo
        spatial-field (auras con background-attachment: fixed → alineación
        exacta) y lo enmascara. z-30: bajo GodBanner (z-40) y el header (z-50). */}
    <div aria-hidden="true" className="sticky top-0 z-30 h-3 -mb-3 spatial-field" />
    <header
      ref={headerRef}
      data-testid="layout-header"
      aria-label={t('common.header', 'Cabecera')}
      className={cn("mx-auto bg-background/80 backdrop-blur-2xl border border-border/70 shadow-elev-1 px-3 py-2 sm:px-5 sticky top-3 z-50 mt-3 mb-1 lg-chrome [&_button]:rounded-full", alignedWorkspace ? "w-full max-w-none rounded-2xl" : "w-[calc(100%-1.5rem)] sm:w-[calc(100%-2rem)] lg:w-[calc(100%-3rem)] max-w-[1600px] rounded-[22px]")}
    >
      <div className="grid grid-cols-[44px_minmax(0,1fr)_44px] items-center gap-x-2 gap-y-1 md:flex md:gap-2">
        {/* Mobile menu — hamburguesa que abre el drawer de navegación. */}
        <Button
          variant="ghost"
          size="icon"
          // `relative` es MÍO —el badge del aviso se posiciona contra este botón— y los
          // 44px son de `dev` (objetivo táctil mínimo). Las dos hacen falta y no se
          // estorban: elegir un lado habría roto el badge o el tamaño del toque.
          className="lg:hidden min-h-[44px] min-w-[44px] shrink-0 relative"
          onClick={() => setMobileMenuOpen(true)}
          aria-label={
            avisoSePinta(avisoStats)
              ? `${t('common.open_menu', 'Abrir menú')} — ${textoAccesibleDelAviso(avisoStats.textos)}`
              : t('common.open_menu', 'Abrir menú')
          }
          data-testid="mobile-menu-trigger"
        >
          <Menu className="w-5 h-5" />
          {/* (b3) · EN MÓVIL LA BARRA LATERAL ES UN CAJÓN detrás de esta hamburguesa, así
              que el badge de la barra NO SE VE hasta abrir el menú — es decir, hay que ir
              a buscarlo, que es exactamente lo que la frase del 1.d descarta.
              Este indicador va sobre el único elemento que SIEMPRE está en pantalla en
              móvil. El número completo sigue en la barra, al abrirla. */}
          {avisoSePinta(avisoStats) && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[9px] font-semibold tabular-nums"
              aria-hidden="true"
              data-testid="badge-movil-pendientes"
            >
              {contenidoDelBadge(avisoStats)}
            </span>
          )}
        </Button>

        {/* Breadcrumb — always renders the current page title text.
            Los tramos de ancestro se ocultan < md: en móvil solo cabe el título
            de la página actual (y se trunca en vez de empujar los iconos). */}
        <Breadcrumb className="min-w-0 flex-1">
          <BreadcrumbList className="flex-nowrap">
            {breadcrumbPath?.parentKey && (
              <>
                <BreadcrumbItem className="text-xs hidden md:inline-flex">
                  <span className="text-muted-foreground">{t(breadcrumbPath.parentKey)}</span>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="hidden md:block" />
              </>
            )}
            {breadcrumbPath?.parentPageName && (
              <>
                <BreadcrumbItem className="text-xs hidden md:inline-flex">
                  <BreadcrumbLink asChild>
                    <Link to={createPageUrl(breadcrumbPath.parentPageName)} className="text-muted-foreground hover:text-foreground">
                      {t(breadcrumbPath.parentTitleKey)}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="hidden md:block" />
              </>
            )}
            <BreadcrumbItem className="min-w-0">
              <BreadcrumbPage className="text-sm font-medium text-foreground truncate">
                {pageTitle}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* En móvil las acciones ocupan una segunda fila; desde md forman una
            sola barra compacta junto al título. */}
        <div className="col-span-3 row-start-2 flex min-w-0 items-center justify-between gap-1 md:ml-auto md:shrink-0 md:justify-end [&_button]:min-h-[44px] [&_button]:min-w-[44px] xl:[&_button]:min-h-[40px] xl:[&_button]:min-w-[40px]">

        {/* ⌘K trigger */}
        <Button
          data-testid="command-menu-trigger"
          variant="outline"
          size="sm"
          className="hidden md:inline-flex w-40 lg:w-48 xl:w-56 h-9 px-3 justify-start border-transparent text-muted-foreground bg-muted/70 hover:bg-muted"
          onClick={() => setShowCommandMenu(true)}
          aria-label={t('common.search', 'Buscar')}
        >
          <Search className="w-3.5 h-3.5 mr-2 opacity-70" />
          <span className="flex-1 text-left text-xs truncate">{t('common.search_placeholder')}</span>
          <kbd className="font-mono text-nano px-1 rounded border border-border bg-card">{typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K'}</kbd>
        </Button>

        {/* Buscar (móvil) — el pill de ⌘K no cabe; misma acción como icono. */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden shrink-0"
          onClick={() => setShowCommandMenu(true)}
          aria-label={t('common.search', 'Buscar')}
          data-testid="command-menu-trigger-mobile"
        >
          <Search className="w-4 h-4" />
        </Button>

        {/* Pulso de ARIA — chip de estado (hechas / esperan tu OK / frenadas).
            Persistente, abre el desglose + acceso a la Bandeja. Se muestra
            cuando hay espacio suficiente para conservar la cabecera en una fila. */}
        <div className="hidden 2xl:flex items-center">
          <AriaPulse companyId={currentCompany?.id} variant="chip" />
        </div>

        {/* Saldo de créditos de IA. Se monta aquí y no solo en el AIPanel porque
            el toggle de abajo NO existe en la superficie de ARIA (`isAriaPage`):
            en la pantalla donde más se gasta, el panel —y con él su medidor— no
            llega a montarse nunca. */}
        <div className="hidden sm:flex items-center">
          <AiCreditsMeter variant="chip" />
        </div>

        {/* IA toggle — solo icono < lg para no comerse el ancho del título.

            NO se monta en la propia superficie de ARIA (`isAriaPage`): abría un
            SEGUNDO asistente (AIPanel, con su historial aparte) encima del panel
            de ARIA a pantalla completa. El chip de AriaPulse de arriba sí se
            queda: es el parte de actividad, no un chat, y es su única superficie
            en el home — por eso `AsistenteAgentic` no monta la banda `full`. */}
        {!isAriaPage && (
          <Button
            variant={aiPanelOpen ? 'secondary' : 'ghost'}
            size="sm"
            onClick={onToggleAIPanel}
            aria-label={aiPanelOpen ? t('ai.panel_close') : t('ai.panel_open')}
            aria-pressed={aiPanelOpen}
            data-testid="ai-panel-toggle"
            className={cn('h-9 shrink-0 px-2 lg:px-3', aiPanelOpen && 'bg-primary/10 text-primary hover:bg-primary/15')}
          >
            <Bot className="w-4 h-4 lg:mr-1.5" />
            <span className="hidden lg:inline text-xs font-medium">ARIA</span>
          </Button>
        )}

        {/* (b3) · EL AVISO DE PENDIENTES VERI*FACTU.
            🔴 Va AQUÍ y SIN `hidden sm:` a propósito: es el único sitio que está en
            pantalla en las tres vistas —ancha, plegada y móvil— sin abrir nada. Y es un
            BOTÓN, así que el emergente con las tres cifras exactas se abre con clic,
            teclado y toque; un tooltip no se abriría nunca en una tableta.
            Se pinta solo si `avisoVisible`, que lo decide el servidor. */}
        <AvisoPendientes stats={avisoStats} />

        {/* Support / bug report — inline trigger (auth-gated, owns its dialog) */}
        <BugReportWidget />

        {/* Notifications — el estado del panel vive aquí para que el aviso de
            bienvenida (useAriaNudge) pueda abrirlo desde su acción. */}
        <NotificationCenter open={bellOpen} onOpenChange={setBellOpen} />

        {/* New — Issue #237: antes era un Button vacío sin onClick.
            Ahora es un DropdownMenu con accesos rápidos a las creaciones
            más frecuentes (factura, cliente, gasto, proveedor, producto). */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {/* El texto aparece cuando cabe junto al resto de acciones. */}
            <Button size="sm" className="h-9 shrink-0 px-2 lg:px-3 shadow-sm" data-testid="topbar-new">
              <Plus className="w-3.5 h-3.5 lg:mr-1" />
              <span className="hidden lg:inline text-xs font-medium">{t('common.new')}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel className="text-xs">Crear nuevo</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to={createPageUrl('FacturaForm')} className="cursor-pointer">
                <FileText className="w-4 h-4 mr-2" />Factura
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/gastos?action=new" className="cursor-pointer">
                <Receipt className="w-4 h-4 mr-2" />Gasto
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/contactos?tab=clientes" className="cursor-pointer">
                <Users className="w-4 h-4 mr-2" />Cliente
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/contactos?tab=proveedores" className="cursor-pointer">
                <Briefcase className="w-4 h-4 mr-2" />Proveedor
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/productos?action=new" className="cursor-pointer">
                <Package className="w-4 h-4 mr-2" />Producto
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        </div>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="col-start-3 row-start-1 min-h-[44px] min-w-[44px] rounded-full xl:min-h-[40px] xl:min-w-[40px]"
              aria-label={t('common.user_menu', 'Menú de usuario')}
              data-testid="user-menu-trigger"
            >
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt={user.full_name} className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center">
                  <UserIcon className="w-4 h-4" />
                </div>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-[calc(100dvh-1.5rem)] w-56 overflow-y-auto">
            <div className="px-2 py-1.5 text-sm">
              <div className="font-semibold">{user?.full_name}</div>
              <div className="truncate text-muted-foreground text-xs" title={user?.email}>{user?.email}</div>
            </div>
            <DropdownMenuSeparator />

            {/* Acciones secundarias reunidas aquí para mantener la cabecera clara. */}
            <DropdownMenuItem onClick={toggleTheme} className="cursor-pointer">
              {theme === 'dark' ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
              {theme === 'dark' ? t('common.light_mode') : t('common.dark_mode')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />

            <DropdownMenuItem onClick={() => setShowKeyboardShortcuts(true)} className="cursor-pointer">
              <Keyboard className="w-4 h-4 mr-2" />
              {t('shortcuts.title', 'Atajos de teclado')}
            </DropdownMenuItem>
            {onStartTour && (
              <DropdownMenuItem onClick={onStartTour} className="cursor-pointer">
                <HelpCircle className="w-4 h-4 mr-2" />
                Guía de inicio
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => window.open('https://docs.verix.es', '_blank', 'noopener,noreferrer')}
              className="cursor-pointer"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              {t('common.documentation', 'Documentación')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />

            <DropdownMenuLabel className="text-nano font-semibold uppercase tracking-wider text-muted-foreground">
              {t('common.density')}
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={density}
              onValueChange={(v) => setDensity(v)}
              data-testid="density-radio-group"
            >
              <DropdownMenuRadioItem value="compact" data-testid="density-option-compact">
                {t('common.density_compact')}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="cozy" data-testid="density-option-cozy">
                {t('common.density_cozy')}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="spacious" data-testid="density-option-spacious">
                {t('common.density_spacious')}
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            {isOverridden && (
              <DropdownMenuItem onClick={clearOverride} className="text-xs text-muted-foreground cursor-pointer">
                <Check className="w-3 h-3 mr-2 opacity-0" />
                {t('common.density_use_default', { value: t(`common.density_${tenantDefault}`) })}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />

            <DropdownMenuLabel className="text-nano font-semibold uppercase tracking-wider text-muted-foreground">
              {t('common.surface', 'Apariencia')}
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={surface}
              onValueChange={(v) => setSurface(v)}
              data-testid="surface-radio-group"
            >
              <DropdownMenuRadioItem value="classic" data-testid="surface-option-classic">
                {t('common.surface_classic', 'Clásico')}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="liquid" data-testid="surface-option-liquid">
                {t('common.surface_liquid', 'Liquid Glass')}
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            {surfaceOverridden && (
              <DropdownMenuItem onClick={clearSurface} className="text-xs text-muted-foreground cursor-pointer">
                <Check className="w-3 h-3 mr-2 opacity-0" />
                {t('common.surface_use_default', {
                  value: t(`common.surface_${surfaceTenantDefault}`),
                  defaultValue: 'Usar el de la empresa',
                })}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />

            {/* FASE 4 Sub-PR 4.3 — switcher empresa accesible desde topbar.
                Solo se renderiza si el user tiene >1 company (oculto, no
                disabled — feedback_hidden_not_disabled_when_no_permission). */}
            {canSwitchCompany && (
              <>
                <DropdownMenuItem
                  onClick={onSwitchCompany}
                  className="cursor-pointer gap-2"
                  data-testid="topbar-switch-company"
                >
                  <Building2 className="w-4 h-4" />
                  <div className="flex flex-col">
                    <span>{t('nav.switch_company', 'Cambiar empresa')}</span>
                    {currentCompany?.name && (
                      <span className="text-xs text-muted-foreground truncate max-w-[180px]">
                        {currentCompany.name}
                      </span>
                    )}
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem asChild>
              <Link to={createPageUrl('Configuracion', { section: 'profile' })} className="cursor-pointer">
                <UserIcon className="w-4 h-4 mr-2" />
                {t('common.profile')}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={createPageUrl('PortalEmpleado')} className="cursor-pointer">
                <UserIcon className="w-4 h-4 mr-2" />{t('nav.my_zone')}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={toggleLanguage} className="cursor-pointer">
              <span className="w-4 h-4 mr-2 text-center text-xs font-bold">{i18n.language === 'es' ? 'EN' : 'ES'}</span>
              {i18n.language === 'es' ? 'English' : 'Español'}
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="cursor-pointer">
                <Globe className="w-4 h-4 mr-2" />
                {t('common.regional_format', 'Formato regional')}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup
                  value={preferences?.display_locale || getDisplayLocale()}
                  onValueChange={(code) => {
                    setDisplayLocale(code);
                    updatePrefs({ display_locale: code });
                  }}
                >
                  {DISPLAY_LOCALES.map((opt) => (
                    <DropdownMenuRadioItem key={opt.code} value={opt.code} className="cursor-pointer">
                      <div className="flex flex-col">
                        <span>{opt.label}</span>
                        <span className="text-xs text-muted-foreground">{opt.example}</span>
                      </div>
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuItem onClick={onLogout} className="cursor-pointer text-destructive">
              <LogOut className="w-4 h-4 mr-2" />
              {t('auth.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
    </>
  );
}
